import axios from './axiosWrapper';
import rawAxios from 'axios'; // For uploading to the presigned URL without our wrapper headers

const CrapEditorApi = {
    // Uploads - v2 decoupled video presign
    getVideoPresignedUrl({ fileName, contentType }) {
        return axios.post('/crap/v2/uploads/video/presign', { fileName, contentType }, { withCredentials: true });
    },
    // Legacy support if needed
    getPresignedUrl({ moduleSlug, topicSlug, fileName, contentType }) {
        return axios.post('/crap/v2/uploads/video/presign', { fileName, contentType }, { withCredentials: true });
    },
    saveUploadedVideo({ moduleSlug, topicSlug, r2Key, fileName, size, contentType }) {
        return axios.post(`/crap/admin/uploads/${moduleSlug}`, { topicSlug, r2Key, fileName, size, contentType }, { withCredentials: true });
    },

    // Metadata (Section 1)
    getMetadata() {
        return axios.get('/crap/v2/metadata', { withCredentials: true });
    },
    saveMetadata(payload) {
        // payload: { introVideoR2Key, text: [{ key, value }] }
        return axios.put('/crap/admin/metadata', payload, { withCredentials: true });
    },

    // Section 2 Modules (C-RAP v2)
    getSection2Modules() {
        return axios.get('/crap/v2/sections/2/modules', { withCredentials: true });
    },
    getSection2Module(moduleId) {
        return axios.get(`/crap/v2/sections/2/modules/${moduleId}`, { withCredentials: true });
    },
    createSection2Module(payload) {
        // payload: { name, videoR2Key, exercisePrompt, pathPrompt, order }
        return axios.post('/crap/v2/sections/2/modules', payload, { withCredentials: true });
    },
    updateSection2Module(moduleId, payload) {
        // payload: { name, videoR2Key, exercisePrompt, pathPrompt, order }
        return axios.put(`/crap/v2/sections/2/modules/${moduleId}`, payload, { withCredentials: true });
    },
    deleteSection2Module(moduleId) {
        return axios.delete(`/crap/v2/sections/2/modules/${moduleId}`, { withCredentials: true });
    },

    // Backward-compatible aliases
    getModules() {
        return axios.get('/crap/v2/sections/2/modules', { withCredentials: true });
    },
    createModule(payload) {
        return axios.post('/crap/v2/sections/2/modules', payload, { withCredentials: true });
    },
    updateModule(moduleID, payload) {
        return axios.put(`/crap/v2/sections/2/modules/${moduleID}`, payload, { withCredentials: true });
    },
    deleteModule(moduleID) {
        return axios.delete(`/crap/v2/sections/2/modules/${moduleID}`, { withCredentials: true });
    },

    // Section 2 AI Exercises & Jobs (Can also be used by Admin/Preview testing)
    generateExercise(moduleId) {
        return axios.post(`/crap/v2/sections/2/modules/${moduleId}/exercise`, {}, { withCredentials: true });
    },
    generatePath(moduleId, payload) {
        // payload: { exerciseId, selectedAnswers } OR { questions, selectedAnswers }
        return axios.post(`/crap/v2/sections/2/modules/${moduleId}/path`, payload, { withCredentials: true });
    },
    getJobStatus(jobId) {
        return axios.get(`/crap/v2/jobs/${jobId}`, { withCredentials: true });
    },
    getExercise(exerciseId) {
        return axios.get(`/crap/v2/exercises/${exerciseId}`, { withCredentials: true });
    },
    getStudentProgress() {
        return axios.get('/crap/v2/student/progress', { withCredentials: true });
    },
    saveStudentProgress(payload) {
        // payload: { moduleId, exerciseNumber, section, exerciseId, currentQuestion, answers, completed }
        return axios.put('/crap/v2/student/progress', payload, { withCredentials: true });
    },

    // Section 3 Assessment Questions (C-RAP v2)
    getSection3Questions(moduleId = 'assessment-modules') {
        return axios.get(`/crap/v2/sections/3/modules/${moduleId}/questions`, { withCredentials: true });
    },
    createSection3Question(moduleId = 'assessment-modules', payload) {
        return axios.post(`/crap/v2/sections/3/modules/${moduleId}/question`, payload, { withCredentials: true });
    },
    updateSection3Question(moduleId = 'assessment-modules', questionId, payload) {
        return axios.put(`/crap/v2/sections/3/modules/${moduleId}/questions/${questionId}`, payload, { withCredentials: true });
    },
    deleteSection3Question(moduleId = 'assessment-modules', questionId) {
        return axios.delete(`/crap/v2/sections/3/modules/${moduleId}/questions/${questionId}`, { withCredentials: true });
    },
    getAssessmentProgress(moduleId = 'assessment-modules') {
        return axios.get(`/crap/v2/sections/3/modules/${moduleId}/assessment-progress`, { withCredentials: true });
    },
    saveAssessmentProgress(moduleId = 'assessment-modules', payload) {
        return axios.put(`/crap/v2/sections/3/modules/${moduleId}/assessment-progress`, payload, { withCredentials: true });
    },
    generateAssessmentQuestion(moduleId = 'assessment-modules', payload) {
        return axios.post(`/crap/v2/sections/3/modules/${moduleId}/assessment-question`, payload, { withCredentials: true });
    },

    // Section 4 Roadmap Prompts & Generation
    getRoadmapPrompt() {
        return axios.get('/crap/v2/sections/4/prompt', { withCredentials: true });
    },
    saveRoadmapPrompt(prompt) {
        // payload: { prompt }
        return axios.put('/crap/v2/sections/4/prompt', { prompt }, { withCredentials: true });
    },
    generateRoadmap() {
        return axios.post('/crap/v2/sections/4/roadmap', {}, { withCredentials: true });
    },

    // Section 5 Prompts & Student Endpoints
    getFocusAreasPrompt(level) {
        return axios.get(`/crap/v2/sections/5/focus-areas/${level}/prompt`, { withCredentials: true });
    },
    saveFocusAreasPrompt(level, prompt) {
        // level: 'easy' | 'medium' | 'advanced'
        return axios.put(`/crap/v2/sections/5/focus-areas/${level}/prompt`, { prompt }, { withCredentials: true });
    },
    getSelfProjectsPrompt() {
        return axios.get('/crap/v2/sections/5/self-projects/prompt', { withCredentials: true });
    },
    saveSelfProjectsPrompt(prompt) {
        return axios.put('/crap/v2/sections/5/self-projects/prompt', { prompt }, { withCredentials: true });
    },
    getSelfProjectsGuidePrompt() {
        return axios.get('/crap/v2/sections/5/self-projects/guide-prompt', { withCredentials: true });
    },
    saveSelfProjectsGuidePrompt(prompt) {
        return axios.put('/crap/v2/sections/5/self-projects/guide-prompt', { prompt }, { withCredentials: true });
    },
    getCertificationsPrompt() {
        return axios.get('/crap/v2/sections/5/certifications/prompt', { withCredentials: true });
    },
    saveCertificationsPrompt(prompt) {
        return axios.put('/crap/v2/sections/5/certifications/prompt', { prompt }, { withCredentials: true });
    },
    getSection5Progress() {
        return axios.get('/crap/v2/sections/5/progress', { withCredentials: true });
    },
    generateFocusAreaTopics(level) {
        return axios.post(`/crap/v2/sections/5/focus-areas/${level}/topics`, {}, { withCredentials: true });
    },
    generateSelfProjects() {
        return axios.post('/crap/v2/sections/5/self-projects', {}, { withCredentials: true });
    },
    generateSelfProjectGuide(projectId) {
        return axios.post(`/crap/v2/sections/5/self-projects/${projectId}/guide`, {}, { withCredentials: true });
    },
    generateCertifications() {
        return axios.post('/crap/v2/sections/5/certifications', {}, { withCredentials: true });
    },

    // Section 6 Internships Prompts & Student Endpoints
    getInternshipsPrompt() {
        return axios.get('/crap/v2/sections/6/internships/prompt', { withCredentials: true });
    },
    saveInternshipsPrompt(prompt) {
        return axios.put('/crap/v2/sections/6/internships/prompt', { prompt }, { withCredentials: true });
    },
    generateInternships() {
        return axios.post('/crap/v2/sections/6/internships/generate', {}, { withCredentials: true });
    },
    getSection6Progress() {
        return axios.get('/crap/v2/sections/6/progress', { withCredentials: true });
    },
    updateInternshipProgress(internshipId, payload) {
        // payload: { completed: true, ... }
        return axios.put(`/crap/v2/sections/6/internships/${internshipId}`, payload, { withCredentials: true });
    },

    // Section 7 CV Endpoints
    getStudentCV() {
        return axios.get('/crap/v2/sections/7/cv', { withCredentials: true });
    },
    saveStudentCV(payload) {
        return axios.post('/crap/v2/sections/7/cv', payload, { withCredentials: true });
    },

    // Student current section tracking
    getCurrentSection() {
        return axios.get('/crap/v2/student/current-section', { withCredentials: true });
    },
    updateCurrentSection(section) {
        return axios.put('/crap/v2/student/current-section', { section: String(section) }, { withCredentials: true });
    },

    // Polling helper for async AI jobs (e.g. assessment-generate, roadmap-generate)
    async pollJobUntilDone(jobId, maxAttempts = 30, intervalMs = 1500) {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            try {
                const res = await axios.get(`/crap/v2/jobs/${jobId}`, { withCredentials: true });
                const data = res.data?.data || res.data;
                if (data && (data.status === 'completed' || data.status === 'SUCCESS' || data.status === 'done')) {
                    return data.result || data.output || data;
                }
                if (data && (data.status === 'failed' || data.status === 'ERROR')) {
                    throw new Error(data.error || 'Job failed during AI execution');
                }
            } catch (err) {
                if (err.message && err.message.includes('failed')) throw err;
                console.warn(`[CrapEditorApi] Job poll attempt ${attempt + 1} warning:`, err);
            }
        }
        throw new Error('AI Generation timed out. Please try again.');
    },

    // College & Degree Utilities (for q1 degrees and q2 departments/specializations)
    getDegrees() {
        return axios.get('/utility/degrees', { withCredentials: true });
    },
    getDepartments(institutionID) {
        return axios.get(`/batch/departments/${institutionID}`, { withCredentials: true });
    },
    getSpecialisations(institutionID, departmentID) {
        return axios.get(`/batch/specialisations/${institutionID}/department/${departmentID}`, { withCredentials: true });
    },

    uploadFileToR2(presignedUrl, file, onUploadProgress) {
        return rawAxios.put(presignedUrl, file, {
            headers: {
                'Content-Type': file.type || 'video/mp4',
            },
            onUploadProgress,
        });
    },
};

export default CrapEditorApi;
